import {
  derivedStream,
  persistentReducedStream,
  streamReducer,
} from '@ardoq/rxbeach';
import { map, shareReplay, startWith } from 'rxjs/operators';
import { scopeDiff$ } from 'scope/scopeDiff$';
import { gridEditor$ } from 'gridEditor/gridEditor$';
import { gridEditorStateOperations as gridEditorStateOperations } from 'gridEditor/gridEditorStateOperations';
import { ScenarioModeState, ScopeDiffState } from '@ardoq/data-model';
import { currentUserPermissionContext$ } from 'streams/currentUserPermissions/currentUserPermissionContext$';
import { isViewSidePanelOpened$ } from '../viewSidePanel/viewSidePanel$';
import { isDetailsDrawerOpen$ } from 'appLayout/ardoqStudio/detailsDrawer/isDetailsDrawerOpen$';
import { context$ } from 'streams/context/context$';
import { workspaceAccessControlInterface } from 'resourcePermissions/accessControlHelpers/workspace';
import { activeScenario$ } from 'streams/activeScenario/activeScenario$';
import { isViewpointMode$ } from '../../../traversals/loadedGraph$';
import { isDiscoverJourney } from 'canvas/isDiscoverJourney';
import {
  hasAIGeneratedScenariosFeature,
  hasScenarioFeature,
} from '@ardoq/features';
import { focusedChrome$ } from 'scope/focusedChrome$';
import { focusedChromeOperations } from 'scope/focusedChromeOperations';

interface StreamShape {
  isScenarioMode: boolean;
  isDiffMode: boolean;
  isGridEditorVisible: boolean;
}

const mainAppGridEditorVisibility$ = gridEditor$.pipe(
  map(gridEditor => {
    return (
      gridEditorStateOperations.isDockedGridEditorState(gridEditor) &&
      gridEditorStateOperations.isDockedGridEditorOpen(gridEditor)
    );
  })
);

const scopeDiffReducer = (
  state: StreamShape,
  { scopeDiff, isLoading }: ScopeDiffState
) => ({
  ...state,
  isDiffMode: Boolean(scopeDiff || isLoading),
});

const mainAppGridEditorVisibilityReducer = (
  state: StreamShape,
  isGridEditorVisible: boolean
) => ({
  ...state,
  isGridEditorVisible,
});

const setIsScenarioMode = (
  state: StreamShape,
  { isScenarioMode }: ScenarioModeState
): StreamShape => ({ ...state, isScenarioMode });

const state$ = persistentReducedStream(
  'componentFabVisibility$',
  {
    isScenarioMode: false,
    isDiffMode: false,
    isGridEditorVisible: false,
  },
  [
    streamReducer(activeScenario$, setIsScenarioMode),
    streamReducer(scopeDiff$, scopeDiffReducer),
    streamReducer(
      mainAppGridEditorVisibility$,
      mainAppGridEditorVisibilityReducer
    ),
  ]
);

const fabVisibility$ = derivedStream(
  'fabVisibility$',
  state$,
  currentUserPermissionContext$,
  isViewSidePanelOpened$,
  isDetailsDrawerOpen$,
  context$,
  activeScenario$,
  isViewpointMode$,
  focusedChrome$
).pipe(
  map(
    ([
      { isGridEditorVisible, isDiffMode, isScenarioMode },
      currentUserPermissionContext,
      isViewSidePanelOpened,
      isDetailsDrawerOpen,
      context,
      activeScenarioState,
      { isViewpointMode },
      focusedChrome,
    ]) => {
      const { workspaceId } = context;
      const isContextWorkspaceWritable =
        workspaceAccessControlInterface.canEditWorkspace(
          currentUserPermissionContext,
          workspaceId,
          activeScenarioState
        );

      const isDiscoverContributorInViewpointMode =
        isViewpointMode && isDiscoverJourney();

      // Scenario mode for non-scenario orgs is limited, and components cannot
      // be added or created in available (AI generated) scenarios
      const isLimitedScenarioMode =
        isScenarioMode &&
        !hasScenarioFeature() &&
        hasAIGeneratedScenariosFeature();

      return (
        !focusedChromeOperations.isReadOnlyVisualization(focusedChrome) &&
        !isLimitedScenarioMode &&
        !isDiscoverContributorInViewpointMode &&
        !isGridEditorVisible &&
        !isDiffMode &&
        isContextWorkspaceWritable &&
        !isViewSidePanelOpened &&
        !isDetailsDrawerOpen
      );
    }
  ),
  startWith(false),
  shareReplay({ bufferSize: 1, refCount: true })
);

export { fabVisibility$ };
